import { STAGE_THEME_COLORS } from '@/theme/stage-colors';
import type { StageId } from '@/types/stage-theme';
import { getConfiguredUiStageId } from '@/utils/stage/stage-utils';

export interface MazeStagePixiPalette {
  readonly floor: number;
  readonly floorAlt: number;
  readonly wallTop: number;
  readonly wallSide: number;
  readonly wallSideDark: number;
  readonly wallShadow: number;
  readonly wallShadowAlpha: number;
  readonly wallHighlight: number;
  readonly playerBase: number;
  readonly playerCore: number;
  readonly playerSpecular: number;
  readonly playerShadow: number;
  readonly playerMask: number;
  readonly beaconOuter: number;
  readonly beaconInner: number;
  readonly trail: number;
  readonly wallGlow: number;
  readonly wallGlowCore: number;
  readonly wallNeonBorder: number;
  readonly wallNeonInset: number;
  readonly wallNeonTopShade: number;
}

const toPixiHex = (hex: string): number => Number.parseInt(hex.replace('#', ''), 16);

const PLAY_FIELD_FLOOR = 0x000000;
const PLAY_FIELD_FLOOR_ALT = 0x141414;

const STAGE_BALL_COLOR: Record<StageId, string> = {
  1: '#19E01A',
  2: '#FF7A59',
  3: '#FF4DB5',
  4: '#FCD309',
  5: '#57D6FF',
  6: '#C16BFF',
  7: '#FCD309',
};

const STAGE_PORTAL_COLOR: Record<StageId, string> = {
  1: '#FCD309',
  2: '#3DA5FF',
  3: '#6E7CFF',
  4: '#39B7FF',
  5: '#2F89FF',
  6: '#9A7BFF',
  7: '#1884FD',
};

// NOTE : This overrides the default staged id based wall color to better match the Figma design, which has a custom wall color and glow
const STAGE_WALL_COLOR: Record<StageId, string | undefined> = {
  1: undefined,
  2: undefined,
  3: undefined,
  4: '#257666',
  5: undefined,
  6: undefined,
  7: undefined,
};

// NOTE : This is a custom glow color matching the Figma design, which has a custom wall color and glow
const STAGE_WALL_GLOW_COLOR: Record<StageId, string | undefined> = {
  1: undefined,
  2: undefined,
  3: undefined,
  4: '#96FCF4',
  5: undefined,
  6: undefined,
  7: undefined,
};

function parseHex(hex: string): { r: number; g: number; b: number } {
  const normalized = hex.replace('#', '');
  const value = Number.parseInt(normalized, 16);
  return {
    r: (value >> 16) & 0xff,
    g: (value >> 8) & 0xff,
    b: value & 0xff,
  };
}

function toHex(r: number, g: number, b: number): string {
  const clamp = (val: number) => Math.max(0, Math.min(255, Math.round(val)));
  return `#${[r, g, b].map((c) => clamp(c).toString(16).padStart(2, '0')).join('')}`;
}

function mixHex(a: string, b: string, ratio: number): string {
  const from = parseHex(a);
  const to = parseHex(b);
  const t = Math.max(0, Math.min(1, ratio));
  return toHex(
    from.r + (to.r - from.r) * t,
    from.g + (to.g - from.g) * t,
    from.b + (to.b - from.b) * t,
  );
}

function lightenHex(hex: string, amount: number): string {
  return mixHex(hex, '#ffffff', amount);
}

function darkenHex(hex: string, amount: number): string {
  return mixHex(hex, '#000000', amount);
}

function createMazePalette(stageId: StageId): MazeStagePixiPalette {
  const stageTheme = STAGE_THEME_COLORS[stageId];
  const customWallColor = STAGE_WALL_COLOR[stageId];
  const wallColor = customWallColor
    ? toPixiHex(customWallColor)
    : toPixiHex(stageTheme.resultAccent);
  const ballColor = toPixiHex(STAGE_BALL_COLOR[stageId]);
  const portalColor = toPixiHex(STAGE_PORTAL_COLOR[stageId]);

  const customGlowColor = STAGE_WALL_GLOW_COLOR[stageId] ?? stageTheme.resultAccent;
  const wallGlowColor = toPixiHex(customGlowColor);
  const wallGlowCoreColor = toPixiHex(lightenHex(customGlowColor, 0.75));
  const wallNeonBorderColor = toPixiHex(lightenHex(customGlowColor, 0.35));
  const wallNeonInsetColor = toPixiHex(lightenHex(customGlowColor, 0.55));
  const wallNeonTopShadeColor = toPixiHex(darkenHex(stageTheme.background, 0.12));
  const playerShadowColor = toPixiHex(darkenHex(STAGE_BALL_COLOR[stageId], 0.22));
  const playerMaskColor = toPixiHex(lightenHex(stageTheme.background, 0.92));

  return {
    floor: PLAY_FIELD_FLOOR,
    floorAlt: PLAY_FIELD_FLOOR_ALT,
    wallTop: wallColor,
    wallSide: wallColor,
    wallSideDark: wallColor,
    wallShadow: 0x000000,
    wallShadowAlpha: 0.25,
    wallHighlight: wallColor,
    playerBase: ballColor,
    playerCore: ballColor,
    playerSpecular: 0xffffff,
    playerShadow: playerShadowColor,
    playerMask: playerMaskColor,
    beaconOuter: portalColor,
    beaconInner: portalColor,
    trail: ballColor,
    wallGlow: wallGlowColor,
    wallGlowCore: wallGlowCoreColor,
    wallNeonBorder: wallNeonBorderColor,
    wallNeonInset: wallNeonInsetColor,
    wallNeonTopShade: wallNeonTopShadeColor,
  };
}

/** Current-stage PIXI palette snapshot for tests and static consumers. */
export const MAZE_PIXI_PALETTE: MazeStagePixiPalette = createMazePalette(getConfiguredUiStageId());

export function getMazePixiPalette(): MazeStagePixiPalette {
  return createMazePalette(getConfiguredUiStageId());
}
